import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth, useCurrentUser, useOperator } from "globals/hooks";
import type { Request } from "types";
import type {
  PaymentMethodSetupHandle,
  PaymentMethodSetupState,
  SavedPaymentMethod,
  SetupAttempt,
} from "components/payment/PaymentMethodSetup/types";
import {
  matchesQuoteSetupReturn,
  retainQuoteSetup,
  clearQuoteSetupReturn,
  type QuoteSetupReturn,
} from "./quoteSetupReturn";
import type { ConfirmReservationFormState } from "./form/schemaValidation";
import type { ProfileSetupCorrelation } from "components/profile/paymentMethodSetupReturn";

export const getQuoteSetupOwnerKey = (
  operatorId: string,
  requestId: string,
  payerId: string,
  authStage: string,
  authenticatedContactId?: string
) =>
  JSON.stringify([
    operatorId,
    requestId,
    payerId,
    authStage,
    authenticatedContactId,
  ]);

export const useQuotePaymentSetup = (
  request: Request,
  returned?: QuoteSetupReturn
) => {
  const { operator } = useOperator();
  const { authStage } = useAuth();
  const currentUser = useCurrentUser();
  const source = {
    operatorId: operator.id,
    requestId: request.id,
    payerId: request.bookingContact.id,
    ownerKey: getQuoteSetupOwnerKey(
      operator.id,
      request.id,
      request.bookingContact.id,
      authStage,
      currentUser?.id
    ),
  };
  const [originalReturn] = useState(() =>
    returned && matchesQuoteSetupReturn(returned, source) ? returned : null
  );
  const [enabled] = useState(
    () => operator.enablePaymentElement === true || !!originalReturn
  );
  const [resume, setResume] = useState(
    originalReturn && !originalReturn.profileSetup ? originalReturn : null
  );
  const [addCardReturn, setAddCardReturn] = useState(
    originalReturn?.profileSetup
      ? {
          ...originalReturn.profileSetup,
          clientSecret: originalReturn.clientSecret,
        }
      : undefined
  );
  const [attempt, setAttempt] = useState<SetupAttempt>();
  const [result, setResult] = useState<SavedPaymentMethod>();
  const resultRef = useRef<SavedPaymentMethod>();
  const elementRef = useRef<PaymentMethodSetupHandle>(null);
  const activeRef = useRef(true);
  const [setupState, setSetupState] = useState<PaymentMethodSetupState>(null);
  const [restartCount, setRestartCount] = useState(0);
  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);
  const onSavedPaymentMethod = useCallback((method: SavedPaymentMethod) => {
    if (!activeRef.current) return;
    resultRef.current = method;
    setResult(method);
    clearQuoteSetupReturn();
  }, []);
  const onStateChange = useCallback(
    (next: PaymentMethodSetupState) => {
      if (!activeRef.current) return;
      setSetupState(next);
      if (next.result) onSavedPaymentMethod(next.result);
    },
    [onSavedPaymentMethod]
  );
  const save = async (_selectedStripeId?: string) => {
    if (!activeRef.current)
      throw new Error("Quote checkout changed. Please try again.");
    if (resultRef.current) return resultRef.current;
    if (!elementRef.current)
      throw new Error("Card form is still loading. Please try again.");
    const method = await elementRef.current.save();
    if (!activeRef.current)
      throw new Error("Quote checkout changed. Please try again.");
    onSavedPaymentMethod(method);
    return method;
  };
  const retain = (
    setupIntentId: string,
    form: ConfirmReservationFormState,
    profileSetup?: ProfileSetupCorrelation
  ) => retainQuoteSetup(source, setupIntentId, form, profileSetup);
  const clearAddCardReturn = () => {
    clearQuoteSetupReturn();
    setAddCardReturn(undefined);
  };
  const restart = () => {
    clearQuoteSetupReturn();
    setResume(null);
    setAttempt(undefined);
    resultRef.current = undefined;
    setResult(undefined);
    setSetupState(null);
    setRestartCount((count) => count + 1);
  };
  return {
    enabled,
    savedPaymentMethods:
      currentUser?.id === request.bookingContact.id
        ? currentUser.paymentMethods || []
        : undefined,
    currency: operator.settings?.currencyCode || "USD",
    source,
    elementRef,
    attempt,
    onAttemptCreated: setAttempt,
    result,
    onSavedPaymentMethod,
    setupState,
    onStateChange,
    resume,
    addCardReturn,
    clearAddCardReturn,
    restoredForm: originalReturn?.form,
    retain,
    restart,
    restartCount,
    save,
  };
};
export type QuotePaymentSetupAdapter = ReturnType<typeof useQuotePaymentSetup>;
